import { ErrorMessage } from 'views/components';
import { OfficePicker } from 'views/pickers';
import PropTypes from 'prop-types';
import { turnsActions } from 'core/Turns';
import { useTranslation } from 'react-i18next';
import { validationSchema } from 'shared/utils';
import {
    ButtonContainer,
    ContainerStyled,
    Content,
    GroupContainer
} from './styled';
import {
    Checkbox,
    DateRangePicker,
    ExpandButton,
    FormGroup,
    Input,
    Modal,
    TimeRangePicker
} from 'mol-ui';
import { Col, Row } from 'reactstrap';
import React, { useEffect } from 'react';
import {
    useDispatch,
    useErrors,
    useObjectState
} from 'hooks';

const DEFAULT_STATE = {
    allDay: true,
    dateFrom: null,
    dateTo: null,
    errors: {},
    office: 0,
    reason: '',
    timeFrom: null,
    timeTo: null
};

const AvailableModal = ({
    active,
    available,
    doctor,
    onToggle
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [state, setState, setStateValue, setStateValues] = useObjectState({ ...DEFAULT_STATE });
    const {
        allDay,
        dateFrom,
        dateTo,
        office,
        reason,
        timeFrom,
        timeTo
    } = state;
    const { clearError, errors, setErrors } = useErrors();

    useEffect(() => {
        if (active) {
            setState({ ...DEFAULT_STATE });
        }
    }, [active, setState]);

    const validate = () => {
        const { errors, isValid } = validationSchema({
            date: [!!dateFrom && !!dateTo, t('Requerido')],
            office_id: [!available || !!office, t('Requerido')],
            reason: [!!reason, t('Requerido')],
            time: [!!((allDay && !available) || (timeFrom && timeTo)), t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    const handleDateChange = (dateFrom, dateTo) => {
        clearError('date');
        setStateValues({
            dateFrom,
            dateTo
        });
    };

    const handleTimePickerAdd = (timeFrom, timeTo) => {
        clearError('time');
        setStateValues({
            timeFrom,
            timeTo
        });
    };

    const onChange = (id, value) => {
        clearError(id);
        setStateValue(id, value);
    };

    const onSubmit = () => {
        if (validate()) {
            const body = {
                ...(!available && { all_day: allDay }),
                ...(doctor && { doctor_id: doctor }),
                from_date: dateFrom,
                from_time: timeFrom,
                ...(office && { office_id: office }),
                reason,
                to_date: dateTo,
                to_time: timeTo
            };
            if (available) {
                dispatch(turnsActions.createAvailableTime(body));
            } else {
                dispatch(turnsActions.createUnavailableTime(body));
            }
            onToggle();
        }
    };

    const aditionalsDays = !available ? t('Días no disponibles adicionales') : t('Días disponibles adicionales');
    const timeRange = `${ t('Rango horario') }: ${ timeFrom && timeTo ? `de ${ timeFrom } a ${ timeTo }` : t('no definido') }`;
    return (
        <Modal active={ active } padding="0px" onToggle={ onToggle }>
            <ContainerStyled available={ available }>
                <Content>
                    <Row>
                        <Col className="text-center" xs="12">
                            { aditionalsDays }
                        </Col>
                        <Col lg="4" md="6" xs="12">
                            <Row>
                                <Col xs="12">
                                    <DateRangePicker
                                        onChange={ handleDateChange }
                                    />
                                </Col>
                                {
                                    errors.date &&
                                    <Col xs="12">
                                        <ErrorMessage relative align="left" error={ errors.date } marginLeft={ 0 } marginTop={ -10 } />
                                    </Col>
                                }
                            </Row>
                        </Col>
                        <Col lg={{ offset: 4, size: 4 }} md="6" xs="12">
                            <Row>
                                <Col xs="12">
                                    <OfficePicker
                                        allowNone={ true }
                                        fontSize="13px"
                                        params={ doctor }
                                        value={ office }
                                        onChange={ value => onChange('office', value) }
                                    />
                                </Col>
                                {
                                    errors.office &&
                                    <Col xs="12">
                                        <ErrorMessage relative align="left" error={ errors.office } marginLeft={ 16 } marginTop={ -10 } />
                                    </Col>
                                }
                            </Row>
                        </Col>
                        <Col xs="12">
                            <FormGroup label="Razón">
                                <Input
                                    error={ !!errors.reason }
                                    height="100px"
                                    type="area"
                                    value={ reason }
                                    onChange={ value => onChange('reason', value) }
                                />
                                <ErrorMessage relative error={ errors.reason } marginLeft={ 0 } />
                            </FormGroup>
                        </Col>
                        <Col xs="12">
                            <FormGroup label={ !available ? t('Tiempo') : timeRange }>
                                <GroupContainer>
                                    {
                                        !available &&
                                        <Checkbox
                                            checked={ allDay }
                                            display="inline-flex"
                                            label={ allDay ? t('Todo el día') : timeRange }
                                            onClick={ value => onChange('allDay', value) }
                                        />
                                    }
                                    {
                                        (!allDay || available) &&
                                        <TimeRangePicker
                                            icon="check"
                                            margin="0px 0px 0px 5px"
                                            onPick={ handleTimePickerAdd }
                                        />
                                    }
                                </GroupContainer>
                                <ErrorMessage relative error={ errors.time } marginLeft={ 0 } />
                            </FormGroup>
                        </Col>
                    </Row>
                </Content>
                <ButtonContainer>
                    <ExpandButton
                        danger
                        caption={ t('Cancelar') }
                        size={ 100 }
                        onClick={ onToggle }
                    />
                    <ExpandButton
                        caption={ t('Crear') }
                        size={ 100 }
                        onClick={ onSubmit }
                    />
                </ButtonContainer>
            </ContainerStyled>
        </Modal>
    );
};

AvailableModal.propTypes = {
    active: PropTypes.bool,
    available: PropTypes.bool,
    doctor: PropTypes.number,
    onToggle: PropTypes.func
};

export default AvailableModal;
